import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { downloadBlob } from "@/shared/utils";

interface TasksPdfRow {
  cells: string[];
}

interface TasksPdfOptions {
  projectName: string;
  projectStatus: string;
  budget: string;
  dateRange: string;
  columns: string[];
  rows: TasksPdfRow[];
  filename: string;
  labels: {
    project: string;
    status: string;
    budget: string;
    dates: string;
    taskList: string;
  };
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function registerCyrillicFonts(doc: jsPDF): Promise<void> {
  const [regularBuf, boldBuf] = await Promise.all([
    fetch("/fonts/Roboto-Regular.ttf").then((r) => r.arrayBuffer()),
    fetch("/fonts/Roboto-Bold.ttf").then((r) => r.arrayBuffer()),
  ]);

  doc.addFileToVFS("Roboto-Regular.ttf", arrayBufferToBase64(regularBuf));
  doc.addFont("Roboto-Regular.ttf", "Roboto", "normal");

  doc.addFileToVFS("Roboto-Bold.ttf", arrayBufferToBase64(boldBuf));
  doc.addFont("Roboto-Bold.ttf", "Roboto", "bold");

  doc.setFont("Roboto");
}

export type { TasksPdfOptions, TasksPdfRow };

export async function generateTasksPdf(options: TasksPdfOptions): Promise<void> {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

  await registerCyrillicFonts(doc);

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  let y = margin;

  doc.setFont("Roboto", "bold");
  doc.setFontSize(18);
  doc.text(`${options.labels.project}: ${options.projectName}`, margin, y);

  doc.setFont("Roboto", "normal");
  doc.setFontSize(11);
  const statusWidth = doc.getTextWidth(options.projectStatus);
  doc.text(options.projectStatus, pageWidth - margin - statusWidth, y);

  y += 10;

  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`${options.labels.budget}: ${options.budget}`, margin, y);

  const datesText = `${options.labels.dates}: ${options.dateRange}`;
  const datesWidth = doc.getTextWidth(datesText);
  doc.text(datesText, pageWidth - margin - datesWidth, y);

  y += 10;

  doc.setTextColor(0);
  doc.setFont("Roboto", "bold");
  doc.setFontSize(13);
  doc.text(options.labels.taskList, margin, y);

  y += 6;

  doc.setFont("Roboto", "normal");

  const bodyData = options.rows.map((row) => row.cells);

  autoTable(doc, {
    startY: y,
    head: [options.columns],
    body: bodyData,
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 9,
      cellPadding: 3,
      font: "Roboto",
    },
    headStyles: {
      fillColor: [41, 128, 186],
      textColor: 255,
      fontStyle: "bold",
      font: "Roboto",
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    columnStyles: {
      0: { cellWidth: 60 },
    },
  });

  const totalPages = doc.getNumberOfPages();
  const dateStr = new Date().toLocaleDateString();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont("Roboto", "normal");
    doc.setFontSize(8);
    doc.setTextColor(150);
    const pageHeight = doc.internal.pageSize.getHeight();
    doc.text(dateStr, margin, pageHeight - 8);
    doc.text(`${i} / ${totalPages}`, pageWidth - margin - doc.getTextWidth(`${i} / ${totalPages}`), pageHeight - 8);
  }

  const blob = doc.output("blob");
  downloadBlob(blob, options.filename);
}
